import { FC } from 'react';
import useContextualPathTransformer from '../../../../../../hooks/use-contextual-path-transformer';
import { ROUTE_SPORTSBOOK_LIVE_PAGE } from '../../../../../router/constants';
import SportsbookTextKeys, { SportsbookTranslations } from '../../../sportsbook-text-keys';
import ExploreTextKeys, { ExploreTranslations } from '../sportsbook-explore-text-keys';
import styled from 'styled-components';
import Icon from '../../../../../icon';
import SportsbookExploreFeaturedLeague from './sportsbook-explore-featured-league';
import { FeaturedContentDto } from '../../../api/sbapi/sbapi-api-model';
import { GameCardGroupsWrapper } from '../../../components/game-card-group/sportsbook-game-card-group';
import SportsbookSelectionTrackingTags from '~/components/sportsbook/lobby/sportsbook-selection-tracking-tags';
import SportsbookContentHeader from '../../../components/header/sportsbook-content-header';

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	margin-bottom: 20px;
`;

const StyledIcon = styled(Icon)`
	fill: var(--sportsbook-explore-featured-live-icon-color, currentcolor);
	object-fit: fill;
`;

type Props = {
	title: string;
	titleSupportive: string;
	tag: string;
	linkLabel?: string;
	linkPath?: string;
	iconType?: string;
	translations: SportsbookTranslations & ExploreTranslations;
	featuredContent: FeaturedContentDto;
};

const SportsbookExploreFeaturedContent: FC<Props> = ({
	title,
	titleSupportive,
	tag,
	linkLabel,
	linkPath,
	iconType,
	translations,
	featuredContent
}) => {
	return (
		<Wrapper>
			<SportsbookContentHeader
				title={title}
				titleSupportive={titleSupportive}
				linkLabel={linkLabel}
				linkPath={linkPath}
				iconElement={iconType ? <StyledIcon type={iconType} width="24" height="24" /> : undefined}
			/>
			<GameCardGroupsWrapper>
				{featuredContent.filters.map(filters => (
					<SportsbookExploreFeaturedLeague
						// Because we know, that the filters are unique and divided by league.
						// If this will change, we need to change the key to something more unique.
						key={`explore_${featuredContent.type}_${JSON.stringify(filters.leagueIds)}`}
						type={featuredContent.type}
						eventCount={filters.metadata.eventCount}
						filters={filters}
						translations={translations}
						tag={tag}
					/>
				))}
			</GameCardGroupsWrapper>
		</Wrapper>
	);
};

/**
 * Display LIVE content based on featured content filters.
 */
export const SportsbookExploreLiveContent: FC<Pick<Props, 'translations' | 'featuredContent'>> = ({
	translations,
	featuredContent
}) => {
	const transformPath = useContextualPathTransformer();

	return (
		<SportsbookExploreFeaturedContent
			title={translations[ExploreTextKeys.LIVE_GAMES_TITLE]}
			titleSupportive={translations[ExploreTextKeys.LIVE_GAMES_SUBTITLE]}
			linkLabel={translations[SportsbookTextKeys.SEE_ALL_LABEL]}
			linkPath={transformPath(ROUTE_SPORTSBOOK_LIVE_PAGE, {
				preserveContexts: true
			})}
			tag={SportsbookSelectionTrackingTags.EXPLORE_LIVE}
			translations={translations}
			featuredContent={featuredContent}
		/>
	);
};

/**
 * Display UPCOMING content based on featured content filters.
 */
export const SportsbookExploreUpcomingContent: FC<Pick<Props, 'translations' | 'featuredContent'>> = ({
	translations,
	featuredContent
}) => (
	<SportsbookExploreFeaturedContent
		title={translations[ExploreTextKeys.UPCOMING_GAMES_TITLE]}
		titleSupportive={translations[ExploreTextKeys.UPCOMING_GAMES_SUBTITLE]}
		translations={translations}
		tag={SportsbookSelectionTrackingTags.EXPLORE_UPCOMING}
		featuredContent={featuredContent}
	/>
);

/**
 * Display HOT LIVE content based on featured content filters.
 */
export const SportsbookExploreHotLiveContent: FC<Pick<Props, 'translations' | 'featuredContent'>> = ({
	translations,
	featuredContent
}) => (
	<SportsbookExploreFeaturedContent
		title={translations[ExploreTextKeys.HOT_LIVE_GAMES_TITLE]}
		titleSupportive={translations[ExploreTextKeys.HOT_LIVE_GAMES_SUBTITLE]}
		translations={translations}
		tag={SportsbookSelectionTrackingTags.EXPLORE_POPULAR_LIVE}
		featuredContent={featuredContent}
	/>
);

/**
 * Display HOT UPCOMING content based on featured content filters.
 */
export const SportsbookExploreHotUpcomingContent: FC<Pick<Props, 'translations' | 'featuredContent'>> = ({
	translations,
	featuredContent
}) => (
	<SportsbookExploreFeaturedContent
		title={translations[ExploreTextKeys.HOT_UPCOMING_GAMES_TITLE]}
		titleSupportive={translations[ExploreTextKeys.HOT_UPCOMING_GAMES_SUBTITLE]}
		translations={translations}
		featuredContent={featuredContent}
		tag={SportsbookSelectionTrackingTags.EXPLORE_POPULAR_UPCOMING}
	/>
);
